import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { districtAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import { AdminPage } from '../Common/admin';

const DistrictCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    districtCode: '',
    districtNameAr: '',
    districtNameEng: '',
    cityId: '',
    isActive: true,
  });
  const [cities, setCities] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadCities = useCallback(async () => {
    try {
      const response = await districtAPI.getCities();
      setCities(response.data || []);
    } catch (err) {
      console.error('Failed to load cities:', err);
    }
  }, []);

  useEffect(() => {
    loadCities();
  }, [loadCities]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.districtCode.trim()) {
      newErrors.districtCode = t('district.create.errors.districtCodeRequired');
    }
    if (!formData.districtNameAr.trim()) {
      newErrors.districtNameAr = t('district.create.errors.nameArRequired');
    }
    if (!formData.districtNameEng.trim()) {
      newErrors.districtNameEng = t('district.create.errors.nameEngRequired');
    }
    if (!formData.cityId) {
      newErrors.cityId = t('district.create.errors.cityRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await districtAPI.create({ ...formData, cityId: parseInt(formData.cityId) });
      setShowSuccessModal(true);
      setTimeout(() => navigate('/district'), 1000);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setErrorMessage(t('district.create.messages.createError'));
        setShowErrorModal(true);
      }
      setLoading(false);
    }
  };

  const textField = (name, label, maxLength) => (
    <div className="ad-form-field">
      <label className="ad-label ad-label-required" htmlFor={name}>
        {label}
      </label>
      <input
        id={name}
        type="text"
        name={name}
        className={`ad-input ${errors[name] ? 'ad-input-error' : ''}`}
        value={formData[name]}
        onChange={handleChange}
        maxLength={maxLength}
      />
      {errors[name] && <span className="ad-error-text">{errors[name]}</span>}
    </div>
  );

  return (
    <AdminPage
      title={t('district.create.title')}
      headerActions={
        <button type="button" className="ad-btn" onClick={() => navigate('/district')}>
          <i className="fas fa-arrow-right"></i>
          {t('district.create.backToList')}
        </button>
      }
    >
      <form onSubmit={handleSubmit}>
        <div className="ad-form">
          <div className="ad-form-grid">
            {textField('districtCode', t('district.create.fields.districtCode'), 20)}
            {textField('districtNameAr', t('district.create.fields.nameAr'), 100)}
            {textField('districtNameEng', t('district.create.fields.nameEng'), 100)}

            <div className="ad-form-field">
              <label className="ad-label ad-label-required" htmlFor="cityId">
                {t('district.create.fields.city')}
              </label>
              <select
                id="cityId"
                name="cityId"
                className={`ad-select ${errors.cityId ? 'ad-input-error' : ''}`}
                value={formData.cityId}
                onChange={handleChange}
              >
                <option value="">{t('district.create.fields.city')}</option>
                {cities.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.cityNameEng}
                  </option>
                ))}
              </select>
              {errors.cityId && (
                <span className="ad-error-text">{errors.cityId}</span>
              )}
            </div>
          </div>

          <div className="ad-form-section">
            <div className="ad-check-row">
              <input
                id="isActive"
                type="checkbox"
                name="isActive"
                className="ad-checkbox"
                checked={formData.isActive}
                onChange={handleChange}
              />
              <label htmlFor="isActive">{t('district.create.fields.isActive')}</label>
            </div>
          </div>
        </div>

        <div className="ad-form-foot">
          <button type="button" className="ad-btn" onClick={() => navigate('/district')}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="ad-btn ad-btn-primary" disabled={loading}>
            {loading ? t('common.loading') : t('district.create.save')}
          </button>
        </div>
      </form>

      <SuccessModal
        show={showSuccessModal}
        message={t('district.create.messages.createSuccess')}
        onClose={() => setShowSuccessModal(false)}
      />
      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </AdminPage>
  );
};

export default DistrictCreate;
